import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button, Avatar, Container, Divider, IconButton, Chip, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import MessageIcon from '@mui/icons-material/Message';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import BlockIcon from '@mui/icons-material/Block';
import { keyframes } from '@mui/system';
import apiClient from '../services/apiClient';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Prescriptions = () => {
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pilotMode, setPilotMode] = useState(false);

  useEffect(() => {
    const init = async () => {
      // Check pilot mode status
      try {
        const response = await apiClient.get('/health/pilot-mode');
        if (response.data) {
          setPilotMode(response.data.pilotMode || false);
        }
      } catch (err) {
        console.warn('Failed to check pilot mode:', err);
      }

      // Fetch prescriptions from API
      try {
        const result = await apiClient.getActivePrescriptions();
        const items = result?.data || result || [];
        setPrescriptions(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error('Failed to load prescriptions:', err);
        setError('Unable to load prescriptions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 4,
          maxWidth: 800,
          width: '100%',
          mx: 'auto',
          position: 'relative',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          animation: `${fadeIn} 0.5s ease-out`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          flexDirection: 'column'
        }}>
          <Avatar
            sx={{
              bgcolor: 'success.main',
              width: 56,
              height: 56,
              mb: 2,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            <LocalPharmacyIcon fontSize="large" />
          </Avatar>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            Prescriptions
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Pilot Mode Blocked Message */}
        {pilotMode && (
          <Alert
            severity="error"
            icon={<BlockIcon />}
            sx={{ mb: 3 }}
          >
            <strong>Feature Disabled in Pilot Mode</strong>
            <Box component="div" sx={{ mt: 1 }}>
              Prescriptions are disabled during the pilot phase to prevent PHI storage.
              This feature will be available after the pilot phase is complete.
            </Box>
          </Alert>
        )}

        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {error && !loading && (
          <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>
        )}

        {/* Empty state */}
        {!loading && !error && prescriptions.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              bgcolor: 'background.paper',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              textAlign: 'center',
              color: 'text.secondary'
            }}
          >
            <Typography>No prescriptions found.</Typography>
          </Paper>
        )}

        {/* Prescriptions list */}
        {!loading && prescriptions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {prescriptions.map((rx, index) => (
                <Box
                  key={rx.id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease',
                    animation: `${fadeIn} 0.5s ease-out ${index * 0.1}s both`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Avatar sx={{ bgcolor: 'success.light', mr: 2 }}>
                      <LocalPharmacyIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {rx.medication_name || rx.medication || 'Unknown medication'}
                        </Typography>
                        <Chip
                          icon={<EventIcon fontSize="small" />}
                          label={formatDate(rx.issued_at || rx.date || rx.created_at)}
                          size="small"
                          sx={{
                            bgcolor: 'background.default',
                            '& .MuiChip-label': { fontSize: '0.75rem' }
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                        {rx.instructions || 'No instructions provided'}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip
                          icon={<PersonIcon fontSize="small" />}
                          label={rx.provider_name || rx.doctor || 'Provider'}
                          size="small"
                          sx={{
                            bgcolor: 'primary.light',
                            color: 'primary.contrastText',
                            '& .MuiChip-label': { fontSize: '0.75rem' }
                          }}
                        />
                        {rx.status && (
                          <Chip
                            label={rx.status}
                            size="small"
                            color={rx.status === 'active' ? 'success' : 'default'}
                          />
                        )}
                        <IconButton
                          size="small"
                          color="info"
                          sx={{ '&:hover': { backgroundColor: 'info.light' } }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<MessageIcon />}
            onClick={() => navigate('/messages')}
            sx={{
              py: 1.5, px: 3, borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              flex: { xs: '1 1 auto', sm: '1 1 0' },
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)' }
            }}
          >
            View Messages
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{
              py: 1.5, px: 3, borderRadius: 2, borderWidth: 2,
              transition: 'all 0.3s ease',
              flex: { xs: '1 1 auto', sm: '1 1 0' },
              '&:hover': { borderWidth: 2, backgroundColor: 'rgba(0, 0, 0, 0.04)' }
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Prescriptions;
